import { zodResolver } from '@hookform/resolvers/zod';
import header from 'assets/header-prevencor-xx.png';
import Footer from 'components/core/Footer';
import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { generateAtestadoPdf } from 'pages/atestado/generateAtestadoPdf';
import { generateRequisicaoPdf } from 'pages/requisicao/generateRequisicaoPdf';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  baixarReceitaPublica,
  buscarReceitaFarmaciaPdf,
  consultarDocumentoPublico,
} from 'services/validacao';
import useSWR from 'swr';
import { getApiErrorMessage, isInfrastructureError } from 'utils/apiErrors';
import { baixarBlobPdf } from 'utils/pdfBlob';
import { z } from 'zod';
import { generateReceitaPdf } from './generateReceitaPdf';

const somenteDigitos = (value = '') => String(value).trim().replace(/\D/g, '');

export const DOCUMENT_VALIDATOR_CONFIG = {
  receita: {
    key: 'receita',
    label: 'Receita',
    title: 'VALIDAÇÃO E DISPENSAÇÃO DE RECEITA',
    subtitle:
      'Confira os dados da prescrição médica abaixo e preencha os dados da farmácia para dispensar.',
    codeLabel: 'CÓDIGO DA RECEITA',
    codeErrorMsg: 'Informe um código de receita válido (somente números).',
    contentTitle: 'MEDICAMENTOS / PRESCRIÇÃO',
    paramKeys: ['receita', 'doc', 'cod'],
    supportsDispense: true,
    routePath: '/receita/validacao',
  },
  atestado: {
    key: 'atestado',
    label: 'Atestado',
    title: 'VALIDAÇÃO DE ATESTADO DIGITAL',
    subtitle: 'Consulte a autenticidade e confira o conteúdo do atestado médico.',
    codeLabel: 'CÓDIGO DO ATESTADO',
    codeErrorMsg: 'Informe um código de atestado válido (somente números).',
    contentTitle: 'CONTEÚDO DO ATESTADO',
    paramKeys: ['atestado', 'doc', 'cod'],
    supportsDispense: false,
    routePath: '/atestado/validacao',
  },
  requisicao: {
    key: 'requisicao',
    label: 'Requisição',
    title: 'VALIDAÇÃO DE REQUISIÇÃO DIGITAL',
    subtitle: 'Consulte a autenticidade e confira os exames/procedimentos solicitados.',
    codeLabel: 'CÓDIGO DA REQUISIÇÃO',
    codeErrorMsg: 'Informe um código de requisição válido (somente números).',
    contentTitle: 'EXAMES / SOLICITAÇÃO',
    paramKeys: ['requisicao', 'doc', 'cod'],
    supportsDispense: false,
    routePath: '/requisicao/validacao',
  },
};

const TIPOS_SUPORTADOS = Object.keys(DOCUMENT_VALIDATOR_CONFIG);

function resolveTipoDocumento({ pathname = '', paramTipo = '', search = '' }) {
  const pathLower = pathname.toLowerCase();
  if (pathLower.startsWith('/atestado/')) return 'atestado';
  if (pathLower.startsWith('/requisicao/')) return 'requisicao';
  if (pathLower.startsWith('/receita/')) return 'receita';

  const normalizedParam = String(paramTipo || '')
    .trim()
    .toLowerCase();
  if (TIPOS_SUPORTADOS.includes(normalizedParam)) {
    return normalizedParam;
  }

  const params = new URLSearchParams(search);
  for (const tipo of TIPOS_SUPORTADOS) {
    if (params.get(tipo)) return tipo;
  }

  return 'receita';
}

function getInitialCodesFromUrl(search = '', tipo = 'receita') {
  const params = new URLSearchParams(search);
  const config = DOCUMENT_VALIDATOR_CONFIG[tipo] || DOCUMENT_VALIDATOR_CONFIG.receita;

  let codDocumento = '';
  for (const key of config.paramKeys) {
    const val = params.get(key);
    if (val) {
      codDocumento = somenteDigitos(val);
      break;
    }
  }

  if (!codDocumento) {
    for (const t of TIPOS_SUPORTADOS) {
      const val = params.get(t);
      if (val) {
        codDocumento = somenteDigitos(val);
        break;
      }
    }
  }

  const codPaciente = somenteDigitos(params.get('paciente') || params.get('pac') || '');

  return {
    codDocumento,
    codPaciente,
  };
}

const createDispensacaoSchema = (codeErrorMsg) =>
  z.object({
    codDocumento: z.string().transform(somenteDigitos).pipe(z.string().min(1, codeErrorMsg)),
    codPaciente: z
      .string()
      .transform(somenteDigitos)
      .pipe(z.string().min(1, 'Informe um código de paciente válido (somente números).')),
    cpfUpdate: z
      .string()
      .transform(somenteDigitos)
      .pipe(
        z.string().superRefine((value, ctx) => {
          if (!value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Informe o CPF do atendente da farmácia',
            });
            return;
          }
          if (value.length !== 11) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Informe um CPF válido do atendente (11 dígitos).',
            });
          }
        })
      ),
    origem: z.string().trim().min(1, 'Informe o nome da farmácia'),
  });

const maskCpf = (cpf = '') => {
  const digits = String(cpf).replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
};

export default function Validacao() {
  const location = useLocation();
  const history = useHistory();
  const routeParams = useParams();

  const tipoAtivo = useMemo(
    () =>
      resolveTipoDocumento({
        pathname: location.pathname,
        paramTipo: routeParams?.tipo,
        search: location.search,
      }),
    [location.pathname, routeParams?.tipo, location.search]
  );

  const config = DOCUMENT_VALIDATOR_CONFIG[tipoAtivo] || DOCUMENT_VALIDATOR_CONFIG.receita;

  const iniciais = useMemo(
    () => getInitialCodesFromUrl(location.search, tipoAtivo),
    [location.search, tipoAtivo]
  );

  const [consultaAtiva, setConsultaAtiva] = useState(() => ({
    tipo: tipoAtivo,
    codDoc: iniciais.codDocumento,
    codPac: iniciais.codPaciente,
  }));

  const [baixadaSessao, setBaixadaSessao] = useState(false);
  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState('');
  const [cpfAtendenteBaixa, setCpfAtendenteBaixa] = useState('');
  const [loadingPdf, setLoadingPdf] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createDispensacaoSchema(config.codeErrorMsg)),
    criteriaMode: 'all',
    defaultValues: {
      codDocumento: iniciais.codDocumento,
      codPaciente: iniciais.codPaciente,
      cpfUpdate: '',
      origem: '',
    },
  });

  useEffect(() => {
    const novosCodigos = getInitialCodesFromUrl(location.search, tipoAtivo);
    setValue('codDocumento', novosCodigos.codDocumento);
    setValue('codPaciente', novosCodigos.codPaciente);
    clearErrors();
    setAlert(false);
    setBaixadaSessao(false);
    setConsultaAtiva({
      tipo: tipoAtivo,
      codDoc: novosCodigos.codDocumento,
      codPac: novosCodigos.codPaciente,
    });
  }, [location.search, tipoAtivo, setValue, clearErrors]);

  const swrKey =
    consultaAtiva.codDoc && consultaAtiva.codPac
      ? ['/validacao', consultaAtiva.tipo, consultaAtiva.codDoc, consultaAtiva.codPac]
      : null;

  const {
    data: documento,
    error: erroConsulta,
    isLoading: carregandoConsulta,
    isValidating: validandoConsulta,
    mutate,
  } = useSWR(
    swrKey,
    () =>
      consultarDocumentoPublico({
        tipo: consultaAtiva.tipo,
        codDoc: consultaAtiva.codDoc,
        codPac: consultaAtiva.codPac,
      }),
    {
      shouldRetryOnError: (err) => isInfrastructureError(err),
      errorRetryCount: 2,
      errorRetryInterval: 3000,
      revalidateOnFocus: false,
    }
  );

  const handleTrocarTipo = (novoTipo) => {
    if (novoTipo === tipoAtivo) return;
    const novaConfig = DOCUMENT_VALIDATOR_CONFIG[novoTipo];
    if (!novaConfig) return;
    const codDocAtual = somenteDigitos(getValues('codDocumento'));
    const codPacAtual = somenteDigitos(getValues('codPaciente'));
    const params = new URLSearchParams();
    if (codDocAtual) params.set(novoTipo, codDocAtual);
    if (codPacAtual) params.set('paciente', codPacAtual);
    const searchStr = params.toString();
    history.push(`${novaConfig.routePath}${searchStr ? `?${searchStr}` : ''}`);
  };

  const handleConsultarManual = (e) => {
    if (e) e.preventDefault();
    const codDoc = somenteDigitos(getValues('codDocumento'));
    const codPac = somenteDigitos(getValues('codPaciente'));

    if (!codDoc) {
      setAlert(true);
      setMessage(config.codeErrorMsg);
      toast.warn(config.codeErrorMsg);
      return;
    }

    if (!codPac) {
      const msgPac = 'Informe um código de paciente válido (somente números).';
      setAlert(true);
      setMessage(msgPac);
      toast.warn(msgPac);
      return;
    }

    setAlert(false);
    setBaixadaSessao(false);

    const paramsValidacao = new URLSearchParams({
      [tipoAtivo]: codDoc,
      paciente: codPac,
    });
    history.replace({ search: `?${paramsValidacao.toString()}` });

    if (
      consultaAtiva.tipo === tipoAtivo &&
      consultaAtiva.codDoc === codDoc &&
      consultaAtiva.codPac === codPac
    ) {
      mutate();
    } else {
      setConsultaAtiva({
        tipo: tipoAtivo,
        codDoc,
        codPac,
      });
    }
  };

  const handleClickPrevencor = () => {
    window.open('//www.prevencormarilia.com.br', '_blank');
  };

  const avisarPrimeiroErro = (erros) => {
    const texto = ['codDocumento', 'codPaciente', 'cpfUpdate', 'origem']
      .map((campo) => erros[campo]?.message)
      .find(Boolean);
    if (!texto) return;
    setAlert(true);
    setMessage(texto);
    toast.warn(texto);
  };

  const abrirOuBaixarPdf = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const janela = window.open(url, '_blank');
    if (!janela) {
      baixarBlobPdf({ blob, fileName });
    }
    window.setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const imprimirReceitaBaixada = async () => {
    const codReceita = somenteDigitos(getValues('codDocumento'));
    const codPaciente = somenteDigitos(getValues('codPaciente'));
    const cpfUpdate = somenteDigitos(getValues('cpfUpdate'));

    if (!codReceita || !codPaciente) {
      toast.warn('Informe o código da receita e do paciente.');
      return;
    }

    if (cpfUpdate.length !== 11) {
      const msg = 'Informe o CPF do atendente (11 dígitos) que realizou a baixa para imprimir.';
      setAlert(true);
      setMessage(msg);
      toast.warn(msg);
      return;
    }

    try {
      setLoadingPdf(true);
      const receita = await buscarReceitaFarmaciaPdf({
        codPaciente,
        codReceita,
        cpfUpdate,
      });
      const blob = await generateReceitaPdf({ receita });
      const fileName = `Receita-Timbrado-Prevencor-${receita?.NOMEPAC ?? 'Paciente'}-${receita?.CODRECEITA}.pdf`;
      abrirOuBaixarPdf(blob, fileName);
      setAlert(false);
      toast.success('PDF da receita gerado.');
    } catch (err) {
      const errorMessage = getApiErrorMessage(err, 'Falha ao imprimir receita!');
      setAlert(true);
      setMessage(errorMessage);
      toast.warn(errorMessage);
    } finally {
      setLoadingPdf(false);
    }
  };

  const imprimirDocumentoPublico = async () => {
    if (!documento) return;
    try {
      setLoadingPdf(true);
      if (documento.tipo === 'atestado') {
        const blob = await generateAtestadoPdf({
          atestado: {
            CODATESTADO: documento.codigoDocumento,
            CODPAC: documento.codPaciente,
            NOMEMED: documento.medico?.nome,
            CRMMED: documento.medico?.crm,
            TIPOATESTADO: documento.subtipo,
            DESCRICAO: documento.conteudo,
          },
        });
        abrirOuBaixarPdf(blob, `Atestado-Prevencor-${documento.codigoDocumento}.pdf`);
        toast.success('PDF do atestado gerado.');
        return;
      }

      if (documento.tipo === 'requisicao') {
        const blob = await generateRequisicaoPdf({
          requisicao: {
            CODREQUISICAO: documento.codigoDocumento,
            CODPAC: documento.codPaciente,
            NOMEMED: documento.medico?.nome,
            CRMMED: documento.medico?.crm,
            TIPOREQUISICAO: documento.subtipo,
            DESCRICAO: documento.conteudo,
          },
        });
        abrirOuBaixarPdf(blob, `Requisicao-Prevencor-${documento.codigoDocumento}.pdf`);
        toast.success('PDF da requisição gerado.');
      }
    } catch {
      toast.warn('Erro ao gerar o PDF do documento.');
    } finally {
      setLoadingPdf(false);
    }
  };

  const validarDispensacaoReceita = handleSubmit(
    async ({ codDocumento, codPaciente, cpfUpdate, origem }) => {
      setBaixadaSessao(false);

      try {
        await baixarReceitaPublica({
          codReceita: codDocumento,
          codPaciente,
          cpfUpdate,
          origem,
        });

        setCpfAtendenteBaixa(cpfUpdate);
        setBaixadaSessao(true);
        setAlert(false);
        toast.success(`Receita baixada com sucesso pelo atendente CPF ${maskCpf(cpfUpdate)}.`);
        mutate();
      } catch (err) {
        const errorMessage = getApiErrorMessage(err, 'Falha ao validar receita!');
        setAlert(true);
        setMessage(errorMessage);
        toast.warn(errorMessage);
      }
    },
    avisarPrimeiroErro
  );

  const erroInfra = isInfrastructureError(erroConsulta);
  const temDocumento = Boolean(documento);
  const carregandoInicial = carregandoConsulta && !temDocumento;
  const receitaJaBaixada =
    documento?.tipo === 'receita' &&
    (String(documento?.status || '').toUpperCase() === 'BAIXADA' || baixadaSessao);

  return (
    <Container className="mb-4 mt-4">
      <Row className="justify-content-center">
        <Col md={8}>
          <img src={header} alt="Logo Prevencor" className="img-fluid" />
          <Card className="border-primary shadow-sm mt-2">
            <Card.Header className="text-center">
              <div className="mb-2">
                <ButtonGroup size="sm" aria-label="Tipo de documento">
                  {TIPOS_SUPORTADOS.map((tipoKey) => {
                    const item = DOCUMENT_VALIDATOR_CONFIG[tipoKey];
                    return (
                      <Button
                        key={tipoKey}
                        variant={tipoAtivo === tipoKey ? 'primary' : 'outline-primary'}
                        onClick={() => handleTrocarTipo(tipoKey)}
                      >
                        {item.label}
                      </Button>
                    );
                  })}
                </ButtonGroup>
              </div>
              <Card.Title className="text-primary fw-bold mb-1">{config.title}</Card.Title>
              {baixadaSessao && (
                <Alert variant="primary" className="mt-2 mb-0">
                  Receita validada com sucesso pelo atendente da farmácia (CPF:{' '}
                  {maskCpf(cpfAtendenteBaixa)}).
                </Alert>
              )}
              {alert && (
                <Alert variant="danger" className="mt-2 mb-0">
                  {message}
                </Alert>
              )}
            </Card.Header>
            <Card.Body>
              <p className="text-center text-primary fw-semibold mb-3">{config.subtitle}</p>

              <Form
                id="validacaoDocumentoForm"
                noValidate
                onSubmit={
                  config.supportsDispense ? validarDispensacaoReceita : handleConsultarManual
                }
              >
                <Row className="mb-3 align-items-end">
                  <Col sm={5}>
                    <Form.Label className="font-weight-bold">{config.codeLabel}</Form.Label>
                    <Form.Control
                      id="inputDocumento"
                      className="text-center"
                      inputMode="numeric"
                      isInvalid={!!errors.codDocumento}
                      {...register('codDocumento')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.codDocumento?.message}
                    </Form.Control.Feedback>
                  </Col>
                  <Col sm={5}>
                    <Form.Label className="font-weight-bold">CÓDIGO DO PACIENTE</Form.Label>
                    <Form.Control
                      id="inputPaciente"
                      className="text-center"
                      inputMode="numeric"
                      isInvalid={!!errors.codPaciente}
                      {...register('codPaciente')}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.codPaciente?.message}
                    </Form.Control.Feedback>
                  </Col>
                  <Col sm={2} className="mt-2 mt-sm-0 d-grid">
                    <Button
                      variant="outline-primary"
                      type="button"
                      onClick={handleConsultarManual}
                      disabled={carregandoConsulta || validandoConsulta}
                    >
                      {carregandoConsulta || validandoConsulta ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        'Consultar'
                      )}
                    </Button>
                  </Col>
                </Row>

                {carregandoInicial && (
                  <div className="text-center py-3">
                    <Spinner animation="border" variant="primary" role="status" />
                    <p className="text-muted small mt-2 mb-0">
                      Consultando autenticidade do documento...
                    </p>
                  </div>
                )}

                {erroConsulta &&
                  (erroInfra ? (
                    <ServiceUnavailableAlert
                      error={erroConsulta}
                      stale={temDocumento}
                      onRetry={() => mutate()}
                    />
                  ) : (
                    <Alert variant="warning" className="text-center">
                      {getApiErrorMessage(
                        erroConsulta,
                        'Documento não encontrado ou código do paciente divergente.'
                      )}
                    </Alert>
                  ))}

                {temDocumento && (
                  <Card className="mb-3 border-success bg-light">
                    <Card.Header className="d-flex justify-content-between align-items-center bg-white">
                      <div>
                        <span className="fw-bold text-dark">
                          Documento #{documento.codigoDocumento}
                        </span>
                        {documento.subtipo ? (
                          <span className="text-muted ms-2">({documento.subtipo})</span>
                        ) : null}
                      </div>
                      <Badge bg={receitaJaBaixada ? 'secondary' : 'success'}>
                        {receitaJaBaixada
                          ? 'RECEITA JÁ DISPENSADA (BAIXADA)'
                          : 'DOCUMENTO AUTÊNTICO'}
                      </Badge>
                    </Card.Header>
                    <Card.Body className="py-2">
                      <Row className="small mb-2">
                        <Col md={6}>
                          <strong>Médico(a):</strong> Dr(a). {documento.medico?.nome || '—'} (CRM:{' '}
                          {documento.medico?.crm || '—'})
                        </Col>
                        <Col md={6} className="text-md-end">
                          <strong>Data de Emissão:</strong> {documento.dataEmissao || '—'}
                        </Col>
                      </Row>
                      <Row className="small mb-2">
                        <Col md={12}>
                          <strong>Paciente (iniciais para conferência LGPD):</strong>{' '}
                          {documento.pacienteNomeMascarado || '—'}{' '}
                          <span className="text-muted">(Cód. {documento.codPaciente})</span>
                        </Col>
                      </Row>

                      {documento.dispensacao && (
                        <Alert variant="info" className="py-2 px-3 small mb-2">
                          <strong>Dados da Dispensação:</strong> Realizada por{' '}
                          <strong>{documento.dispensacao.origem || 'Farmácia'}</strong>
                          {documento.dispensacao.cpfMascarado
                            ? ` (CPF: ${documento.dispensacao.cpfMascarado})`
                            : ''}
                          {documento.dispensacao.dataBaixa
                            ? ` em ${documento.dispensacao.dataBaixa}`
                            : ''}
                          .
                        </Alert>
                      )}

                      <div className="mt-2">
                        <div className="small fw-bold text-secondary mb-1">
                          {config.contentTitle} (CONFERÊNCIA):
                        </div>
                        <div
                          className="p-2 bg-white border rounded small"
                          style={{
                            whiteSpace: 'pre-wrap',
                            maxHeight: '220px',
                            overflowY: 'auto',
                          }}
                        >
                          {documento.conteudo || 'Sem descrição detalhada.'}
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}

                {config.supportsDispense && (
                  <>
                    <hr />
                    <Row className="mb-3">
                      <Col>
                        <Form.Label className="font-weight-bold text-center">
                          CPF DO ATENDENTE DA FARMÁCIA (QUEM REALIZOU A BAIXA)
                        </Form.Label>
                        <Form.Control
                          id="inputCPF"
                          className="text-center"
                          placeholder="Digite o CPF do atendente que está dispensando"
                          inputMode="numeric"
                          maxLength={14}
                          isInvalid={!!errors.cpfUpdate}
                          {...register('cpfUpdate')}
                        />
                        <Form.Text className="text-muted text-center">
                          Não é o CPF do médico prescritor.
                        </Form.Text>
                        <Form.Control.Feedback type="invalid">
                          {errors.cpfUpdate?.message}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                    {!receitaJaBaixada && (
                      <Row className="mb-3">
                        <Col>
                          <Form.Label className="font-weight-bold text-center">
                            FARMÁCIA (Nome Fantasia)
                          </Form.Label>
                          <Form.Control
                            id="inputFarmacia"
                            className="text-center"
                            placeholder="Ex.: Farmácia Central"
                            isInvalid={!!errors.origem}
                            {...register('origem')}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.origem?.message}
                          </Form.Control.Feedback>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </Form>
            </Card.Body>
            <Card.Footer className="text-center">
              {config.supportsDispense ? (
                <>
                  <Button
                    variant="outline-success"
                    type="submit"
                    form="validacaoDocumentoForm"
                    disabled={receitaJaBaixada}
                  >
                    {receitaJaBaixada ? 'Receita Já Baixada' : 'Validar e Dispensar'}
                  </Button>{' '}
                  <Button
                    variant="outline-primary"
                    onClick={imprimirReceitaBaixada}
                    disabled={!receitaJaBaixada || loadingPdf}
                  >
                    {loadingPdf ? <Spinner animation="border" size="sm" /> : 'Imprimir'}
                  </Button>{' '}
                </>
              ) : (
                <>
                  <Button
                    variant="outline-primary"
                    onClick={imprimirDocumentoPublico}
                    disabled={!temDocumento || loadingPdf}
                  >
                    {loadingPdf ? <Spinner animation="border" size="sm" /> : 'Imprimir PDF'}
                  </Button>{' '}
                </>
              )}
              <Button variant="outline-danger" onClick={handleClickPrevencor}>
                Fechar
              </Button>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <Footer />
    </Container>
  );
}
